import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Unlock, History, Undo2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ist = (s, withYear) => {
  try {
    return new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
      ...(withYear ? { year: "numeric" } : {}), hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return s || ""; }
};

export const useDocEditLock = () => {
  const [until, setUntil] = useState("");
  const refresh = () => axios.get(`${API}/doc-edit/status`, { headers: getAuthHeader() })
    .then(r => setUntil(r.data.unlocked_until || "")).catch(() => {});
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, []);
  const open = !!until && new Date(until) > new Date();
  return { until, open, setUntil, refresh };
};

export const DocEditUnlockDialog = ({ open, onClose, onUnlocked }) => {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/doc-edit/unlock`, { password: pwd }, { headers: getAuthHeader() });
      toast.success(`Cell editing unlocked for 3 hours (${r.data.via})`);
      setPwd(""); onUnlocked(r.data.unlocked_until);
    } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="doc-edit-unlock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-amber-600" /> Unlock cell editing</DialogTitle>
          <DialogDescription>Enter your own login password, or an admin / supervisor password. Editing stays open for 3 hours on every Packing List and Bulk Data file. Every change is recorded.</DialogDescription>
        </DialogHeader>
        <Input type="password" autoFocus value={pwd} onChange={e => setPwd(e.target.value)} onKeyDown={e => e.key === "Enter" && pwd && go()}
               placeholder="Password" data-testid="doc-edit-unlock-password" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!pwd || busy} onClick={go} data-testid="doc-edit-unlock-submit">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Unlock</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DocEditLockChip = ({ lock, onUnlock }) => lock.open ? (
  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700"
        title="Double-click any cell to edit" data-testid="doc-edit-lock-open">
    <Unlock className="w-3.5 h-3.5" /> Editing open till {new Date(lock.until).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })}</span>
) : (
  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={onUnlock} data-testid="doc-edit-unlock-btn">
    <Lock className="w-4 h-4 mr-1.5" /> Unlock editing</Button>
);

export const CentralEditLog = ({ module, label }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ by: "", date_from: "", date_to: "" });
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return;
    setD(null);
    axios.get(`${API}/doc-cell-edits`, { params: { module, ...f }, headers: getAuthHeader() })
      .then(r => setD(r.data)).catch(() => setD({ people: [], rows: [] }));
  }, [open, f, module]);
  const openFile = (e) => window.open(`/preview?module=${module}&id=${e.entry_id}`, "_blank");
  const set = (k) => (ev) => setF(p => ({ ...p, [k]: ev.target.value }));
  return (<>
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid={`central-edit-log-btn-${module}`}>
      <History className="w-3.5 h-3.5 mr-1" /> Edit log</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-6xl" data-testid="central-edit-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-slate-600" /> {label} — on-screen edit log</DialogTitle>
          <DialogDescription>Every cell changed on screen across all {label} files, newest first. Click a file to open it.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select value={f.by} onChange={set("by")} className="h-8 rounded border px-2" data-testid="central-edit-log-person">
            <option value="">Everyone</option>{(d?.people || []).map(p => <option key={p} value={p}>{p}</option>)}</select>
          <label className="flex items-center gap-1">From<input type="date" value={f.date_from} onChange={set("date_from")} className="h-8 rounded border px-2" data-testid="central-edit-log-from" /></label>
          <label className="flex items-center gap-1">To<input type="date" value={f.date_to} onChange={set("date_to")} className="h-8 rounded border px-2" data-testid="central-edit-log-to" /></label>
          {(f.by || f.date_from || f.date_to) && <Button size="sm" variant="ghost" className="h-8" onClick={() => setF({ by: "", date_from: "", date_to: "" })}>Clear</Button>}
          <span className="ml-auto text-gray-500" data-testid="central-edit-log-count">{d ? `${d.rows.length} change(s)` : ""}</span>
        </div>
        <div className="max-h-[30rem] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-100"><tr>
              {["When (IST)", "By", "File", "Invoice", "Cell", "Column", "Old", "New", "Status"].map(h => <th key={h} className="border border-gray-200 px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
            <tbody>
              {!d && <tr><td colSpan={9} className="py-6 text-center"><Loader2 className="mx-auto w-4 h-4 animate-spin" /></td></tr>}
              {d?.rows.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-gray-400">No on-screen edits.</td></tr>}
              {d?.rows.map(e => (
                <tr key={e.id} className={e.undone ? "text-gray-400" : "even:bg-gray-50/60"} data-testid={`central-edit-row-${e.id}`}>
                  <td className="whitespace-nowrap border border-gray-200 px-2 py-1">{ist(e.at, true)}</td>
                  <td className="border border-gray-200 px-2 py-1">{e.by_name || e.by}</td>
                  <td className="border border-gray-200 px-2 py-1"><button className="text-left text-sky-700 underline" onClick={() => openFile(e)}>{e.filename}</button></td>
                  <td className="border border-gray-200 px-2 py-1">{e.invoice_no}</td>
                  <td className="border border-gray-200 px-2 py-1 font-mono">{e.sheet_name}!{e.cell}</td>
                  <td className="border border-gray-200 px-2 py-1">{e.column_name}</td>
                  <td className="max-w-[160px] break-words border border-gray-200 px-2 py-1 text-rose-700">{e.old || "(blank)"}</td>
                  <td className="max-w-[160px] break-words border border-gray-200 px-2 py-1 font-semibold text-emerald-700">{e.new || "(blank)"}</td>
                  <td className="whitespace-nowrap border border-gray-200 px-2 py-1">{e.undone ? `Undone ${ist(e.undone_at)}` : "Live"}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  </>);
};

export const CellEditHistory = ({ open, onClose, module, entryId, lockOpen, needUnlock, onUndone }) => {
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${API}/doc-preview/${module}/${entryId}/cell-edits`, { headers: getAuthHeader() })
    .then(r => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { if (open) load(); }, [open]);
  const undo = async (e) => {
    if (!lockOpen) { needUnlock(); return; }
    try {
      const r = await axios.post(`${API}/doc-cell-edits/${e.id}/undo`, {}, { headers: getAuthHeader() });
      toast.success(`${e.cell} put back to "${e.old || "(blank)"}"${r.data.overwrote_later_change ? " — a later change to this cell was replaced" : ""}`);
      onUndone(r.data); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Undo failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="cell-edit-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-slate-600" /> Edit history</DialogTitle>
          <DialogDescription>Every on-screen cell change in this file, newest first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[28rem] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-100"><tr>
              {["When (IST)", "By", "Sheet", "Cell", "Column", "Old", "New", ""].map(h => <th key={h} className="border border-gray-200 px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
            <tbody>
              {rows === null && <tr><td colSpan={8} className="py-6 text-center"><Loader2 className="mx-auto w-4 h-4 animate-spin" /></td></tr>}
              {rows?.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-gray-400">No cell edits yet.</td></tr>}
              {rows?.map(e => (
                <tr key={e.id} className={e.undone ? "bg-gray-50 text-gray-400" : "even:bg-gray-50/60"} data-testid={`cell-edit-row-${e.id}`}>
                  <td className="whitespace-nowrap border border-gray-200 px-2 py-1">{ist(e.at, true)}</td>
                  <td className="border border-gray-200 px-2 py-1">{e.by_name || e.by}</td>
                  <td className="border border-gray-200 px-2 py-1">{e.sheet_name}</td>
                  <td className="border border-gray-200 px-2 py-1 font-mono">{e.cell}</td>
                  <td className="border border-gray-200 px-2 py-1">{e.column_name}</td>
                  <td className="max-w-[180px] break-words border border-gray-200 px-2 py-1 text-rose-700">{e.old || "(blank)"}</td>
                  <td className="max-w-[180px] break-words border border-gray-200 px-2 py-1 font-semibold text-emerald-700">{e.new || "(blank)"}</td>
                  <td className="whitespace-nowrap border border-gray-200 px-2 py-1">
                    {e.undone ? <span title={`by ${e.undone_by_name || e.undone_by}`}>Undone {ist(e.undone_at)}</span>
                      : <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => undo(e)} data-testid={`cell-edit-undo-${e.id}`}>
                          <Undo2 className="w-3 h-3 mr-1" /> Undo</Button>}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
